import { FC } from 'react';
import { Redirect, Switch } from 'react-router-dom';
import { useGetRole } from 'hooks/ui/useGetRole';
import RestrictedRoute from './RestrictedRoute';
import CommonRoutes from './CommonRoutes';

import OpenTrancheDashboard from '../Pages/OpenTrancheDashboard';
import FirmDashboard from '../Pages/FirmDashboard';
import ClosedTranchePage from '../Pages/AllTranches';
import ClosedTrancheDashboard from '../Pages/ClosedTrancheDashboard';
import ClosedTrancheAddUpdatePage from '../Pages/Tranche/TrancheForm';
import TrancheDataRoom from '../Pages/Tranche/TrancheDataRoom';
import FurtherUserPage from '../Pages/FurtherUsers';
import InvestorPage from '../Pages/Investor';
import InvestorDetailPage from '../Pages/Investor/ViewInvestor';
import InvestorForm from '../Pages/Investor/InvestorForm';
import InvestmentPage from '../Pages/Investment';
import UploadInvestorInvestment from '../Pages/UploadInvestorInvestment';
import OngoingAml from '../Pages/OngoingAml';
import PartiallyInvestmentPage from '../Pages/Investment/IncompleteInvestment';
import ViewInvestmentPage from '../Pages/Investment/ViewInvestment';
import AdminUserPage from '../Pages/AdminUsers';
import AddAdminUserPage from '../Pages/AdminUsers/UserForm';
import FirmUserFormPage from '../Pages/AdminUsers/FirmUserForm';
import InvestmentFirmPage from '../Pages/InvestmentFirm';
import TagPage from '../Pages/Tag';
import AddTagPage from '../Pages/Tag/TagForm';
import TranchePage from '../Pages/Tranche';
import EisWizard from '../Pages/EisWizard/ListCertificates';
import CompanyPage from '../Pages/Company';
import AddUpdateCompanyPage from '../Pages/Company/CompanyForm';
import AddUpdateEisWizardPage from '../Pages/EisWizard/EisWizardForm';
import InterestPaymentListPage from '../Pages/InterestPayment';
import InterestPaymentCreatePage from '../Pages/InterestPayment/InterestPaymentCreate';
import AllocationHistory from '../Pages/AllocationHistory';
import CreateAllocation from '../Pages/Allocations/CreateAllocation';
import AdviserList from '../Pages/Adviser/AdviserList';
import AddUpdateAdviser from '../Pages/Adviser/AdviserForm';
import ViewAdviser from '../Pages/Adviser/ViewAdviser';
import Organisation from '../Pages/Organisation/ListOrganisations';
import AddUpdateOrganisation from '../Pages/Organisation/AddUpdateOrganisation';
import ViewOrganisation from '../Pages/Organisation/ViewOrganisation';
import AddOrganisationUser from '../Pages/Organisation/AddOrganisationUser';
import InvestorReporting from '../Pages/InvestorReporting/View';
import AddUpdateInvestorReporting from '../Pages/InvestorReporting/InvestorReportingWizard';
import InvestorReportingSuccess from '../Pages/InvestorReporting/InvestorReportingWizard/Success';
import InvestorReportingPdfDownload from '../Pages/InvestorReporting/InvestorReportingPdfDownload';
import FeeSummary from '../Pages/FeeSummary';
import AddFeeAndDiscount from '../Pages/FeeManagement/AddFeeAndDiscount';
import CoreFeeTemplates from '../Pages/FeeManagement/CoreFeeTemplates';
import PerformanceFeeTemplates from '../Pages/FeeManagement/PerformanceFeeTemplates';
import OneOffFeesAndDiscounts from '../Pages/FeeManagement/OneOffFeesAndDiscounts';
import FeeAndDiscountSuccess from '../Pages/FeeManagement/AddFeeAndDiscount/Success';
import ReviewWithdrawals from '../Pages/Withdrawals/ReviewWithdrawals';
import RequestWithdrawals from '../Pages/Withdrawals/RequestWithdrawals';
import CreateAndEditExit from '../Pages/Exits/ExitForm';
import ListExits from '../Pages/Exits/ListExits';
import InvestmentFirmAddEditScreen from '../Pages/InvestmentFirmAddEdit';
import AddShareSplit from '../Pages/ShareSplit/AddShareSplit';
import CustomUpdate from '../Pages/CustomUpdate/Add';
import ViewCustomUpdate from '../Pages/CustomUpdate/View';
import DeploymentUpdate from '../Pages/DeploymentUpdate/Add';
import ViewDeploymentUpdate from '../Pages/DeploymentUpdate/View';
import ViewExitStatement from '../Pages/Exits/ViewStatement';
import AddInvestmentTransfer from '../Pages/InvestmentTransfer/AddInvestmentTransfer';
import InvestmentTransferList from '../Pages/InvestmentTransfer/InvestmentTransferList';
import { TrancheLink } from '../Pages/Tranche/TrancheLink';
import InvestorBulletin from '../Pages/InvestorBulletin';
import AddInvestment from '../Pages/Investment/AddInvestment';
import KiFundCertificateBatchForm from '../Pages/KiFundCertificate/CertificateBatchForm';
import TaxReportAudit from '../Pages/TaxReportAudit';
import ChangeLog from '../Pages/ChangeLog';
import ExitStatementPdfDownload from '../Pages/InvestorReporting/ExitStatementPdfDownload';
import BulkPaymentAssignment from '../Pages/BulkPaymentAssignment';
import CreateExitStatement from '../Pages/Exits/CreateExitStatement';
import UpdateExitStatement from '../Pages/Exits/UpdateExitStatement';
import CashTransactionsReport from '../Pages/SystemExports/CashTransactions';
import InvestmentHoldingsReport from '../Pages/SystemExports/InvestmentHoldings';
import InvestmentTransactionsReport from '../Pages/SystemExports/InvestmentTransactions';
import PortalUsageDataReport from '../Pages/SystemExports/PortalUsageData';
import AddEditCoreFeeTemplate from '../Pages/FeeManagement/AddEditCoreFeeTemplate';
import AddEditPerformanceFeeTemplate from '../Pages/FeeManagement/AddEditPerformanceFeeTemplate';
import LpFirmAddEditScreen from '../LpPages/LpFirm';
import BillingPage from '../Pages/Billing';
import AdminUserRolesPage from '../Pages/AdminUserRoles';
import AdminUserRoleCreateEditPage from '../Pages/AdminUserRoles/AddEditRole';
import AdminUserRoleViewPage from '../Pages/AdminUserRoles/ViewRole';
import LinkedPortals from '../Pages/LinkedPortals';
import AddNewLinkedPortals from '../Pages/LinkedPortals/AddNewLinkedPortals';
import DataTools from '../Pages/DataTools';
import ProfilePage from '../Pages/Profile/EisProfile';

import {
  ADVISER,
  ADVISER_ADD,
  ADVISER_EDIT,
  ADVISER_VIEW,
  ALL_FUNDS,
  BULK_PAYMENT_ASSIGNMENT,
  COMPANY,
  COMPANY_ADD,
  COMPANY_EDIT,
  DASHBOARD,
  DASHBOARD_ALL_FIRMS,
  DASHBOARD_CLOSED_TRANCHE,
  DASHBOARD_MY_FIRM,
  DASHBOARD_OPEN_TRANCHE,
  DASHBOARD_SINGLE_FIRM,
  EIS,
  EIS_ADD,
  KI_EIS_ADD,
  EIS_EDIT,
  EXITS,
  EXITS_ADD,
  EXITS_CORRECT,
  EXIT_STATEMENT_PDF_DOWNLOAD,
  CORE_FEE_TEMPLATES,
  CORE_FEE_TEMPLATE_ADD,
  CORE_FEE_TEMPLATE_EDIT,
  PERFORMANCE_FEE_TEMPLATES,
  PERFORMANCE_FEE_TEMPLATE_ADD,
  PERFORMANCE_FEE_TEMPLATE_EDIT,
  FEE_DISCOUNT_ADD,
  FEE_DISCOUNT_SUCCESS,
  FEE_SUMMARY,
  FIRM,
  FIRM_ADD,
  FIRM_EDIT,
  SUBSCRIPTION_FUNNEL,
  INTEREST_PAYMENTS,
  INTEREST_PAYMENTS_ADD,
  INVESTMENT,
  INVESTMENT_VIEW,
  INVESTMENT_EDIT_LEGACY,
  INVESTOR,
  INVESTOR_VIEW,
  INVESTOR_EDIT,
  INVESTOR_REPORTING,
  INVESTOR_REPORTING_ADD,
  INVESTOR_REPORTING_EDIT,
  INVESTOR_REPORTING_SUCCESS,
  INVESTOR_REPORTING_VIEW,
  INVESTOR_REPORTING_PDF_DOWNLOAD,
  INVESTOR_UPLOAD,
  ONGOING_AML_REVIEW,
  ORGANISATION,
  ORGANISATION_ADD,
  ORGANISATION_EDIT,
  ORGANISATION_VIEW,
  ORGANISATION_ADD_USER,
  TAG,
  TAG_ADD,
  TAG_EDIT,
  TRANCHE,
  TRANCHE_ADD,
  TRANCHE_EDIT,
  TRANCHE_EDIT_DATA_ROOM,
  ALLOCATION_HISTORY,
  ADMIN_USER,
  ADMIN_USER_ADD,
  ADMIN_USER_EDIT,
  FIRM_USER_ADD,
  FIRM_USER_EDIT,
  WITHDRAWALS,
  WITHDRAWALS_ADD,
  SHARE_SPLIT_ADD,
  ADD_CUSTOM_UPDATE,
  EDIT_CUSTOM_UPDATE,
  VIEW_CUSTOM_UPDATE,
  ADD_DEPLOYMENT_UPDATE,
  ADD_EXIT_STATEMENT,
  UPDATE_EXIT_STATEMENT,
  VIEW_DEPLOYMENT_UPDATE,
  VIEW_EXIT_STATEMENT,
  EXITS_EDIT,
  INVESTOR_ADD,
  ADD_INVESTMENT_TRANSFER,
  INVESTMENT_TRANSFERS,
  TRANCHE_BESPOKE_LINK,
  INVESTOR_BULLETIN,
  INVESTMENT_ADD,
  KI_EIS_EDIT,
  TAX_REPORT_AUDIT,
  CHANGE_LOG,
  ONE_OFF_FEES_AND_DISCOUNTS,
  CASH_TRANSACTIONS_REPORT,
  INVESTMENT_TRANSACTIONS_REPORT,
  INVESTMENT_HOLDINGS_REPORT,
  PORTAL_USAGE_DATA_REPORT,
  LP_FIRM_ADD,
  COMPANY_DUPLICATE,
  BILLING,
  CREATE_ALLOCATION,
  VIEW_DRAFT_ALLOCATION,
  VIEW_ALLOCATION,
  FURTHER_USER,
  ADMIN_USER_ROLES,
  ADMIN_USER_ROLE_CREATE,
  ADMIN_USER_ROLE_EDIT,
  ADMIN_USER_ROLE_VIEW,
  LINKED_PORTALS,
  LINKED_PORTALS_ADD,
  DATA_TOOLS,
  PROFILE,
} from 'adminConstants/routes';

const DashboardRedirectRoute = () => {
  const { isSuperAdmin } = useGetRole();

  if (isSuperAdmin) {
    return <Redirect to={INVESTOR} />;
  }
  return <Redirect to={DASHBOARD_MY_FIRM} />;
};

const InvestmentEditRedirectRoute = ({ match }) => {
  return <Redirect to={`/view-investment/${match.params.id}`} />;
};

const EisRoutes: FC = () => (
  <Switch>
    <RestrictedRoute path="/" exact component={DashboardRedirectRoute} />
    <RestrictedRoute
      component={DashboardRedirectRoute}
      exact
      path={DASHBOARD}
    />
    <RestrictedRoute
      component={OpenTrancheDashboard}
      path={DASHBOARD_OPEN_TRANCHE}
    />
    <RestrictedRoute
      component={ClosedTrancheDashboard}
      path={DASHBOARD_CLOSED_TRANCHE}
    />
    <RestrictedRoute component={FirmDashboard} path={DASHBOARD_SINGLE_FIRM} />
    <RestrictedRoute component={FirmDashboard} path={DASHBOARD_MY_FIRM} />
    <RestrictedRoute component={FirmDashboard} path={DASHBOARD_ALL_FIRMS} />

    <RestrictedRoute component={ClosedTranchePage} path={ALL_FUNDS} />
    <RestrictedRoute component={TranchePage} path={TRANCHE} />
    <RestrictedRoute
      component={ClosedTrancheAddUpdatePage}
      path={TRANCHE_ADD}
    />
    <RestrictedRoute component={TrancheLink} path={TRANCHE_BESPOKE_LINK} />
    <RestrictedRoute
      component={TrancheDataRoom}
      path={TRANCHE_EDIT_DATA_ROOM}
      exact
    />
    <RestrictedRoute
      component={ClosedTrancheAddUpdatePage}
      path={TRANCHE_EDIT}
    />
    <RestrictedRoute component={FurtherUserPage} path={FURTHER_USER} />
    <RestrictedRoute component={InvestorPage} path={INVESTOR} />
    <RestrictedRoute component={InvestorDetailPage} path={INVESTOR_VIEW} />
    <RestrictedRoute component={InvestorForm} path={INVESTOR_EDIT} />
    <RestrictedRoute component={InvestorForm} path={INVESTOR_ADD} />
    <RestrictedRoute
      component={UploadInvestorInvestment}
      path={INVESTOR_UPLOAD}
    />
    <RestrictedRoute component={OngoingAml} path={ONGOING_AML_REVIEW} />
    <RestrictedRoute component={InvestmentPage} path={INVESTMENT} />
    <RestrictedRoute component={AddInvestment} path={INVESTMENT_ADD} />
    <RestrictedRoute
      component={PartiallyInvestmentPage}
      path={SUBSCRIPTION_FUNNEL}
    />
    <RestrictedRoute
      component={InvestmentEditRedirectRoute}
      path={INVESTMENT_EDIT_LEGACY}
    />
    <RestrictedRoute component={ViewInvestmentPage} path={INVESTMENT_VIEW} />
    <RestrictedRoute
      component={InterestPaymentListPage}
      exact
      path={INTEREST_PAYMENTS}
    />
    <RestrictedRoute
      component={InterestPaymentCreatePage}
      exact
      path={INTEREST_PAYMENTS_ADD}
    />
    <RestrictedRoute component={AllocationHistory} path={ALLOCATION_HISTORY} />
    <RestrictedRoute
      component={BulkPaymentAssignment}
      path={BULK_PAYMENT_ASSIGNMENT}
    />
    <RestrictedRoute
      component={CreateAllocation}
      path={VIEW_DRAFT_ALLOCATION}
    />
    <RestrictedRoute component={CreateAllocation} path={VIEW_ALLOCATION} />
    <RestrictedRoute component={CreateAllocation} path={CREATE_ALLOCATION} />
    <RestrictedRoute component={AdminUserPage} path={ADMIN_USER} />
    <RestrictedRoute component={AddAdminUserPage} path={ADMIN_USER_ADD} />
    <RestrictedRoute component={AddAdminUserPage} path={ADMIN_USER_EDIT} />
    <RestrictedRoute component={FirmUserFormPage} path={FIRM_USER_ADD} />
    <RestrictedRoute component={FirmUserFormPage} path={FIRM_USER_EDIT} />
    <RestrictedRoute component={InvestmentFirmPage} path={FIRM} />
    <RestrictedRoute component={InvestmentFirmAddEditScreen} path={FIRM_ADD} />
    <RestrictedRoute component={LpFirmAddEditScreen} path={LP_FIRM_ADD} />
    <RestrictedRoute component={InvestmentFirmAddEditScreen} path={FIRM_EDIT} />
    <RestrictedRoute component={TagPage} path={TAG} />
    <RestrictedRoute component={BillingPage} path={BILLING} />
    <RestrictedRoute component={AddTagPage} path={TAG_ADD} />
    <RestrictedRoute component={AddTagPage} path={TAG_EDIT} />
    <RestrictedRoute component={CompanyPage} path={COMPANY} />
    <RestrictedRoute component={AddUpdateCompanyPage} path={COMPANY_ADD} />
    <RestrictedRoute component={AddUpdateCompanyPage} path={COMPANY_EDIT} />
    <RestrictedRoute
      component={AddUpdateCompanyPage}
      path={COMPANY_DUPLICATE}
    />
    <RestrictedRoute component={EisWizard} path={EIS} />
    <RestrictedRoute component={AddUpdateEisWizardPage} path={EIS_ADD} />
    <RestrictedRoute component={AddUpdateEisWizardPage} path={EIS_EDIT} />
    <RestrictedRoute component={KiFundCertificateBatchForm} path={KI_EIS_ADD} />
    <RestrictedRoute
      component={KiFundCertificateBatchForm}
      path={KI_EIS_EDIT}
    />
    <RestrictedRoute component={AddFeeAndDiscount} path={FEE_DISCOUNT_ADD} />
    <RestrictedRoute component={CoreFeeTemplates} path={CORE_FEE_TEMPLATES} />
    <RestrictedRoute
      component={AddEditCoreFeeTemplate}
      path={CORE_FEE_TEMPLATE_ADD}
    />
    <RestrictedRoute
      component={AddEditCoreFeeTemplate}
      path={CORE_FEE_TEMPLATE_EDIT}
    />
    <RestrictedRoute
      component={PerformanceFeeTemplates}
      path={PERFORMANCE_FEE_TEMPLATES}
    />
    <RestrictedRoute
      component={AddEditPerformanceFeeTemplate}
      path={PERFORMANCE_FEE_TEMPLATE_ADD}
    />
    <RestrictedRoute
      component={AddEditPerformanceFeeTemplate}
      path={PERFORMANCE_FEE_TEMPLATE_EDIT}
    />

    <RestrictedRoute
      component={OneOffFeesAndDiscounts}
      path={ONE_OFF_FEES_AND_DISCOUNTS}
    />
    <RestrictedRoute
      component={FeeAndDiscountSuccess}
      path={FEE_DISCOUNT_SUCCESS}
    />
    <RestrictedRoute component={AdviserList} path={ADVISER} />
    <RestrictedRoute component={AddUpdateAdviser} path={ADVISER_ADD} />
    <RestrictedRoute component={ViewAdviser} path={ADVISER_VIEW} />
    <RestrictedRoute component={AddUpdateAdviser} path={ADVISER_EDIT} />
    <RestrictedRoute component={Organisation} path={ORGANISATION} />
    <RestrictedRoute
      component={AddUpdateOrganisation}
      path={ORGANISATION_ADD}
    />
    <RestrictedRoute
      component={AddUpdateOrganisation}
      path={ORGANISATION_EDIT}
    />
    <RestrictedRoute component={ViewOrganisation} path={ORGANISATION_VIEW} />
    <RestrictedRoute
      component={AddOrganisationUser}
      path={ORGANISATION_ADD_USER}
    />
    <RestrictedRoute
      component={InvestorReporting}
      path={INVESTOR_REPORTING}
      exact
    />
    <RestrictedRoute
      component={AddUpdateInvestorReporting}
      path={INVESTOR_REPORTING_EDIT}
    />
    <RestrictedRoute
      component={AddUpdateInvestorReporting}
      path={INVESTOR_REPORTING_ADD}
    />
    <RestrictedRoute
      component={AddUpdateInvestorReporting}
      path={INVESTOR_REPORTING_VIEW}
    />
    <RestrictedRoute
      component={InvestorReportingSuccess}
      path={INVESTOR_REPORTING_SUCCESS}
    />
    <RestrictedRoute
      component={InvestorReportingPdfDownload}
      path={INVESTOR_REPORTING_PDF_DOWNLOAD}
    />
    <RestrictedRoute
      component={ExitStatementPdfDownload}
      path={EXIT_STATEMENT_PDF_DOWNLOAD}
    />
    <RestrictedRoute component={CustomUpdate} path={ADD_CUSTOM_UPDATE} />
    <RestrictedRoute component={CustomUpdate} path={EDIT_CUSTOM_UPDATE} />
    <RestrictedRoute component={ViewCustomUpdate} path={VIEW_CUSTOM_UPDATE} />
    <RestrictedRoute component={InvestorBulletin} path={INVESTOR_BULLETIN} />
    <RestrictedRoute component={TaxReportAudit} path={TAX_REPORT_AUDIT} />
    <RestrictedRoute component={FeeSummary} path={FEE_SUMMARY} />
    <RestrictedRoute component={RequestWithdrawals} path={WITHDRAWALS_ADD} />
    <RestrictedRoute component={ReviewWithdrawals} path={WITHDRAWALS} />
    <RestrictedRoute component={CreateAndEditExit} path={EXITS_ADD} />
    <RestrictedRoute component={CreateAndEditExit} path={EXITS_EDIT} />
    <RestrictedRoute component={CreateAndEditExit} path={EXITS_CORRECT} />
    <RestrictedRoute component={ListExits} path={EXITS} />
    <RestrictedRoute component={AddShareSplit} path={SHARE_SPLIT_ADD} />
    <RestrictedRoute
      component={DeploymentUpdate}
      path={ADD_DEPLOYMENT_UPDATE}
    />
    <RestrictedRoute
      component={CreateExitStatement}
      path={ADD_EXIT_STATEMENT}
    />
    <RestrictedRoute
      component={UpdateExitStatement}
      path={UPDATE_EXIT_STATEMENT}
    />
    <RestrictedRoute
      component={ViewDeploymentUpdate}
      path={VIEW_DEPLOYMENT_UPDATE}
    />
    <RestrictedRoute component={ViewExitStatement} path={VIEW_EXIT_STATEMENT} />
    <RestrictedRoute
      component={InvestmentTransferList}
      path={INVESTMENT_TRANSFERS}
    />
    <RestrictedRoute
      component={AddInvestmentTransfer}
      path={ADD_INVESTMENT_TRANSFER}
    />
    <RestrictedRoute path={CHANGE_LOG} component={ChangeLog} />
    <RestrictedRoute
      path={CASH_TRANSACTIONS_REPORT}
      component={CashTransactionsReport}
    />
    <RestrictedRoute
      path={INVESTMENT_HOLDINGS_REPORT}
      component={InvestmentHoldingsReport}
    />
    <RestrictedRoute
      path={INVESTMENT_TRANSACTIONS_REPORT}
      component={InvestmentTransactionsReport}
    />
    <RestrictedRoute
      path={PORTAL_USAGE_DATA_REPORT}
      component={PortalUsageDataReport}
    />
    <RestrictedRoute
      path={ADMIN_USER_ROLES}
      exact
      component={AdminUserRolesPage}
    />
    <RestrictedRoute
      path={ADMIN_USER_ROLE_CREATE}
      component={AdminUserRoleCreateEditPage}
    />
    <RestrictedRoute
      path={ADMIN_USER_ROLE_EDIT}
      component={AdminUserRoleCreateEditPage}
    />
    <RestrictedRoute
      path={ADMIN_USER_ROLE_VIEW}
      component={AdminUserRoleViewPage}
    />
    <RestrictedRoute component={LinkedPortals} path={LINKED_PORTALS} />
    <RestrictedRoute
      component={AddNewLinkedPortals}
      path={LINKED_PORTALS_ADD}
    />
    <RestrictedRoute component={DataTools} path={DATA_TOOLS} />
    <RestrictedRoute component={ProfilePage} path={PROFILE} />
    {...CommonRoutes}
  </Switch>
);

export default EisRoutes;
